// reducers/social.js
import {
  SOCIAL_SET_LOADING,
  SOCIAL_SET_TOKENS,
  SOCIAL_SET_ERROR,
  SOCIAL_CONNECT_REQUEST,
  SOCIAL_CONNECT_URL_READY,
  SOCIAL_CONNECT_PAYLOAD,
  SOCIAL_CONNECT_SUCCESS,
  SOCIAL_CONNECT_FAILURE,
  SOCIAL_DISCONNECT_SUCCESS,
  SOCIAL_ACCOUNTS_REQUEST,
  SOCIAL_ACCOUNTS_SUCCESS,
  SOCIAL_ACCOUNTS_FAILURE,
  SOCIAL_TARGETS_LOADED,
  SOCIAL_TARGET_SELECTED,
  SOCIAL_MESSAGE,
} from '../constants/action-types/social';

// ---- New action types (export if you want to dispatch by constant) ----
export const SOCIAL_DRAFT_CLEAR           = 'SOCIAL_DRAFT_CLEAR';
export const SOCIAL_DRAFT_REKEY           = 'SOCIAL_DRAFT_REKEY';
export const SOCIAL_DRAFT_COMMIT_REQUEST  = 'SOCIAL_DRAFT_COMMIT_REQUEST';
export const SOCIAL_DRAFT_COMMIT_SUCCESS  = 'SOCIAL_DRAFT_COMMIT_SUCCESS';
export const SOCIAL_DRAFT_COMMIT_FAILURE  = 'SOCIAL_DRAFT_COMMIT_FAILURE';
// add near your other local action strings
export const SOCIAL_DRAFT_CLEAR_ALL = 'SOCIAL_DRAFT_CLEAR_ALL';

// New action strings (same style as your local ones)
export const SOCIAL_SESSION_HYDRATE = 'SOCIAL_SESSION_HYDRATE';
export const SOCIAL_SESSION_CLEAR   = 'SOCIAL_SESSION_CLEAR';

const initialState = {
  tokens: {},
  loading: false,
  error: null,
  message: null,

  // Legacy, global-by-platform (kept for existing screens)
  connections: {},

  // NEW: brandSettings-scoped drafts (unconfirmed)
  // drafts[brandSettingsId][platform] = { ...same shape as connections[platform] }
  drafts: {},

  // Cache of accounts (kept in two shapes for selector compat)
  accountsById: {},
  accountsLoadedAt: null,
  accounts: {
    byId: {},
    list: []
  }
};

function basePlatformBucket() {
  return {
    status: 'disconnected',
    accountId: null,
    user: null,
    targets: [],
    selectedTargetId: null,
    adsAccounts: [],
    selectedAdsId: null,
    lastUpdated: 0
  };
}

function ensurePlatform(state, platform) {
  const key = (platform || 'unknown').toLowerCase();
  return state.connections[key] || basePlatformBucket();
}

function ensureDraftBucket(state, brandSettingsId) {
  const id = String(brandSettingsId || 'new');
  return state.drafts[id] || {};
}

function ensureDraft(state, brandSettingsId, platform) {
  const id = String(brandSettingsId || 'new');
  const plat = String(platform || 'unknown').toLowerCase();
  const bucket = ensureDraftBucket(state, id);
  return bucket[plat] || basePlatformBucket();
}

function writeDraft(state, brandSettingsId, platform, patch) {
  const id = String(brandSettingsId || 'new');
  const plat = String(platform || 'unknown').toLowerCase();
  const prevDraftPlat = ensureDraft(state, id, plat);
  const nextPlat = { ...prevDraftPlat, ...patch, lastUpdated: Date.now() };
  return {
    ...state,
    drafts: {
      ...state.drafts,
      [id]: {
        ...(state.drafts[id] || {}),
        [plat]: nextPlat
      }
    }
  };
}

function writeConnection(state, platform, patch) {
  const plat = String(platform || 'unknown').toLowerCase();
  const prev = ensurePlatform(state, plat);
  const next = { ...prev, ...patch, lastUpdated: Date.now() };
  return {
    ...state,
    connections: { ...state.connections, [plat]: next }
  };
  
}


// Local helpers to compute status and normalize shapes for connections
function statusFromAccount(acc) {
  if (!acc) return 'disconnected';
  if (acc.status) return String(acc.status);
  if (acc.expiresAt) {
    try { return new Date(acc.expiresAt) > new Date() ? 'connected' : 'expired'; } catch (_) {}
  }
  const looksConnected =
    !!acc.accessToken ||
    !!acc.refreshToken ||
    (Array.isArray(acc.pages) && acc.pages.length > 0);
  return looksConnected ? 'connected' : 'disconnected';
}
function pickBestStatus(list = []) {
  const statuses = list.map(statusFromAccount);
  if (statuses.includes('connected')) return 'connected';
  if (statuses.includes('expired'))   return 'expired';
  if (statuses.includes('failed'))    return 'failed';
  return 'disconnected';
}
const toId = (v) => (v == null ? null : String(v));

// Normalize targets coming from BrandSettings.byPlatform.targets (may be docs)
function normalizeTargetsForConnection(platform, arr = []) {
  const p = String(platform || '').toLowerCase();
  return (arr || []).map((t) => {
    const externalId = toId(t?.externalId ?? t?.id ?? t?.pageId ?? t?.channelId ?? t?.username ?? t?._id);
    return {
      id: externalId, // connections store expects `id` as external
      name: t?.name || t?.pageName || t?.title || t?.username || externalId,
      type: t?.type || `${p}_target`,
      avatarUrl: t?.avatarUrl || t?.pictureUrl || t?.icon || null
    };
  });
}
function normalizeAdsForConnection(arr = []) {
  return (arr || []).map((a) => {
    const id = toId(a?.id ?? a?.accountId ?? a?._id);
    return {
      id,
      accountId: id,
      name: a?.name || a?.accountName || id
    };
  });
}

export default function socialReducer(state = initialState, action) {
  switch (action.type) {
    case SOCIAL_SET_LOADING:
      return { ...state, loading: action.payload };



       /* =========================
       SESSION HYDRATE / CLEAR
       ========================= */
    case SOCIAL_SESSION_CLEAR: {
      // Clear the ephemeral session-level connections (used by modal overlay)
      return {
        ...state,
        connections: {},
        message: null,
        error: null,
      };
    }

    case SOCIAL_SESSION_HYDRATE: {
      // payload: { brandId, brandSettingsId, byPlatform, selectedByPlatform }
      const { byPlatform = {}, selectedByPlatform = {} } = action.payload || {};
      const nextConnections = { };

      Object.keys(byPlatform).forEach((platRaw) => {
        const plat = String(platRaw || 'unknown').toLowerCase();
        const bucket = byPlatform[plat] || {};
        const accounts = Array.isArray(bucket.accounts) ? bucket.accounts : [];
        const targets  = Array.isArray(bucket.targets)  ? bucket.targets  : [];
        const ads      = Array.isArray(bucket.adsAccounts) ? bucket.adsAccounts : [];

        const sel = selectedByPlatform[plat] || {};
        const selAccountId = toId(sel.accountId)
          || (accounts[0]?._id ? String(accounts[0]._id) : null);
        const selTargetDoc  = sel.targetId
          ? (targets.find(t => String(t?._id) === String(sel.targetId)) || null)
          : null;

        // Prefer defaultTarget bridge if present
        const selectedAccountDoc = accounts.find(a => String(a?._id) === String(selAccountId)) || accounts[0] || null;

        let selectedTargetExternalId =
          (sel.externalTargetId
            || (selTargetDoc ? (selTargetDoc.externalId || selTargetDoc.id || selTargetDoc._id) : null)
            || (selectedAccountDoc?.defaultTarget?.id)
          ) || null;

        // fallbacks
        if (!selectedTargetExternalId && targets.length > 0) {
          selectedTargetExternalId = String(targets[0].externalId || targets[0].id || targets[0]._id);
        }

        const normalizedTargets = normalizeTargetsForConnection(plat, targets);
        const normalizedAds = normalizeAdsForConnection(ads);

        // ads selection
        const selAdsId =
          toId(sel.adsId || sel.adsAccountId)
          || (selectedAccountDoc?.defaultTarget?.adsAccountId ? String(selectedAccountDoc.defaultTarget.adsAccountId) : null)
          || (normalizedAds[0]?.id || null);

        // compute status
        const status = selectedAccountDoc ? statusFromAccount(selectedAccountDoc) : pickBestStatus(accounts);

        nextConnections[plat] = {
          ...(ensurePlatform(state, plat)), // keep prior shape keys
          status,
          accountId: selAccountId || null,
          user: selectedAccountDoc?.accountName ? { name: selectedAccountDoc.accountName } : null,
          targets: normalizedTargets,
          selectedTargetId: selectedTargetExternalId ? String(selectedTargetExternalId) : null,
          adsAccounts: normalizedAds,
          selectedAdsId: selAdsId ? String(selAdsId) : null,
          lastUpdated: Date.now()
        };
      });

      return {
        ...state,
        connections: nextConnections
      };
    }



    
    case SOCIAL_SET_TOKENS:
      return {
        ...state,
        tokens: { ...state.tokens, [action.platform]: action.payload }
      };

    case SOCIAL_SET_ERROR:
      return { ...state, error: action.payload };

    case SOCIAL_MESSAGE:
      return { ...state, message: action.payload };

    /* =========================
       CONNECT FLOW
       ========================= */
    case SOCIAL_CONNECT_REQUEST: {
      const { platform, brandSettingsId } = action;
      if (brandSettingsId) {
        return {
          ...writeDraft(state, brandSettingsId, platform, { status: 'connecting' }),
          loading: true,
          error: null,
          message: null
        };
      }
      return {
        ...writeConnection(state, platform, { status: 'connecting' }),
        loading: true,
        error: null,
        message: null
      };
    }

    case SOCIAL_CONNECT_URL_READY:
      return { ...state, loading: false };

    case SOCIAL_CONNECT_PAYLOAD: {
      const p = (action.payload.platform || action.platform || 'unknown').toLowerCase();
      const brandSettingsId = action.brandSettingsId || action.payload.brandSettingsId || null;

      const targets = (action.payload.targets || []).map(t => ({
        id: String(t.id),
        name: t.name,
        type: t.type,
        token: t.token || null,
        avatarUrl: t.avatarUrl || null
      }));
      // Auto-pick first target/ads for initial display (user preference).
      // Backend is kept in sync by SMC's onChange auto-save when the user
      // picks something different.
      const selectedTargetId = targets.length ? String(targets[0].id) : null;
      const adsAccounts = action.payload.adsAccounts || [];
      const selectedAdsId = adsAccounts.length ? String(adsAccounts[0].id || adsAccounts[0].accountId) : null;

      const patch = {
        status: 'connected',
        accountId: String(action.payload.accountId || ''),
        user: action.payload.user || null,
        targets,
        selectedTargetId,
        adsAccounts,
        selectedAdsId
      };

      if (brandSettingsId) {
        return { ...writeDraft(state, brandSettingsId, p, patch), message: null, error: null };
      }
      return { ...writeConnection(state, p, patch), message: null, error: null };
    }

    case SOCIAL_CONNECT_SUCCESS: {
      const { platform, brandSettingsId, accountId } = action;
      const patch = { status: 'connected', accountId: String(accountId || '') };
      if (brandSettingsId) return writeDraft(state, brandSettingsId, platform, patch);
      return writeConnection(state, platform, patch);
    }

    case SOCIAL_CONNECT_FAILURE: {
      const { platform, brandSettingsId, error } = action;
      const patch = { status: 'failed' };
      const base = brandSettingsId
        ? writeDraft(state, brandSettingsId, platform, patch)
        : writeConnection(state, platform, patch);
      return { ...base, loading: false, error: error || 'Connection failed' };
    }

    case SOCIAL_DISCONNECT_SUCCESS: {
      const { platform, brandSettingsId } = action;
      const patch = {
        status: 'disconnected',
        accountId: null,
        user: null,
        targets: [],
        selectedTargetId: null,
        adsAccounts: [],
        selectedAdsId: null
      };
      if (brandSettingsId) return writeDraft(state, brandSettingsId, platform, patch);
      return writeConnection(state, platform, patch);
    }

    /* =========================
       ACCOUNTS LIST (cache + legacy)
       ========================= */
    case SOCIAL_ACCOUNTS_REQUEST:
      return { ...state, loading: true, error: null };

    case SOCIAL_ACCOUNTS_SUCCESS: {
      const byId = { ...state.accountsById };
      const connections = { ...state.connections };
      const list = [];

      (action.payload || []).forEach(acc => {
        const platform = (acc.platform || 'unknown').toLowerCase();
        const id = String(acc._id);
        byId[id] = acc;
        list.push(acc);

        const prev = ensurePlatform(state, platform);
        const status = acc.disconnected ? 'disconnected' : 'connected';
        connections[platform] = {
          ...prev,
          status,
          accountId: status === 'connected' ? id : null,
          user: acc.accountName ? { name: acc.accountName } : prev.user,
          lastUpdated: Date.now()
        };
      });

      return {
        ...state,
        loading: false,
        accountsById: byId,
        accountsLoadedAt: Date.now(),
        connections,
        // keep the legacy shapes many selectors/components look for
        accounts: {
          byId,
          list
        }
      };
    }

    case SOCIAL_ACCOUNTS_FAILURE:
      return { ...state, loading: false, error: action.error || 'Failed to load accounts' };

    /* =========================
       TARGETS
       ========================= */
    case SOCIAL_TARGETS_LOADED: {
      const p = (action.platform || 'unknown').toLowerCase();
      const { brandSettingsId } = action;
      const targets = (action.payload || []).map(t => ({
        id: String(t.id),
        name: t.name,
        type: t.type,
        token: t.token || null,
        avatarUrl: t.avatarUrl || null
      }));
      const patch = { targets, selectedTargetId: targets.length ? String(targets[0].id) : null };
      if (brandSettingsId) return writeDraft(state, brandSettingsId, p, patch);
      return writeConnection(state, p, patch);
    }

    case SOCIAL_TARGET_SELECTED: {
      const p = (action.platform || 'unknown').toLowerCase();
      const { brandSettingsId } = action;
      const getPrev = () => brandSettingsId ? ensureDraft(state, brandSettingsId, p) : ensurePlatform(state, p);

      const prev = getPrev();
      const { target, adsAccountId } = action.payload || {};
      const nextTargets = (() => {
        const map = new Map((prev.targets || []).map(t => [String(t.id), t]));
        if (target?.id) map.set(String(target.id), target);
        return Array.from(map.values());
      })();
      const patch = {
        targets: nextTargets,
        selectedTargetId: target?.id ? String(target.id) : prev.selectedTargetId,
        selectedAdsId: adsAccountId || prev.selectedAdsId
      };
      if (brandSettingsId) return writeDraft(state, brandSettingsId, p, patch);
      return writeConnection(state, p, patch);
    }

    /* =========================
       DRAFT LIFECYCLE
       ========================= */
       

       case SOCIAL_DRAFT_CLEAR_ALL: {
  return { ...state, drafts: {} };
}

case 'ADD_NEW_BRAND':
case 'UPSERT_BRAND': {
  // when brand is persisted, clean any draft for that brandSettings
  const bsid = String(
    action.payload?.brandSettings?._id ||
    action.payload?.brandSettingsId ||
    ''
  );
  const drafts = { ...state.drafts };
  if (bsid && drafts[bsid]) delete drafts[bsid];
  if (drafts['new']) delete drafts['new']; // clean temp
  return { ...state, drafts };
}



    case SOCIAL_DRAFT_CLEAR: {
      const { brandSettingsId } = action;
      const id = String(brandSettingsId || 'new');
      if (!state.drafts[id]) return state;
      const drafts = { ...state.drafts };
      delete drafts[id];
      return { ...state, drafts };
    }

    

    case SOCIAL_DRAFT_REKEY: {
      const { from, to } = action; // move tmp -> real brandSettingsId
      if (!from || !to || !state.drafts[from]) return state;
      const drafts = { ...state.drafts };
      drafts[to] = { ...(drafts[to] || {}), ...drafts[from] };
      delete drafts[from];
      return { ...state, drafts };
    }

    case SOCIAL_DRAFT_COMMIT_REQUEST:
      return { ...state, loading: true };
    case SOCIAL_DRAFT_COMMIT_SUCCESS:
      return { ...state, loading: false };
    case SOCIAL_DRAFT_COMMIT_FAILURE:
      return { ...state, loading: false, error: action.error || 'Commit failed' };

    default:
      return state;
  }
}
